import { addSorted, sortedUnique } from './collections.js';
import { advanced } from './clock.js';
import { canSelect, composerForIdentity, requiredSlotsFilled, selectionOf, slotAt, previewText, } from './composer.js';
import { runGates } from './gates.js';
import { deriveHint } from './hints.js';
import { harvestKey } from './pack.js';
import { deliverDuePosts, postMessage, withSuspicion } from './outcomes.js';
import { bandwidthCostOf, freeBandwidth, puppetSelectable, releasePossession, swapGrants, takePossession, touchPuppet, } from './possession.js';
import { evalContextFor, proposalAfterLanding, proposalAfterSend, resolveAt } from './resolve.js';
import { aggregateTagSets } from './tags.js';
import { buriedOutcomeId, burialDepth, emitNoise, enterPhase } from './world.js';
import { identityNodeId, puppetIdentity, } from './state.js';
const unchanged = (state) => ({
    state,
    effects: [],
    trace: null,
    traces: [],
});
// ---------------------------------------------------------------------------
// The disaster undo allowance
// ---------------------------------------------------------------------------
/**
 * Whether the profile still has an undo to spend.
 *
 * The allowance is counted against undos actually *taken*, not offers made: an
 * offer the player declined cost them nothing and should not have been their
 * one chance. Consuming it is what is recorded, and what the Quarterly Review
 * charges for through `precision.undoPenalty`.
 */
export function undoAvailable(state) {
    return state.progress.undosUsed < state.settings.disasterUndoAllowance;
}
/**
 * Holds the pre-send state where `UNDO_SEND` can reach it, when the send that
 * just happened was a disaster the profile is willing to walk back.
 *
 * A disaster that *ended* the mission is not offered: `reduce` refuses every
 * action once `end` is set, so an offer there would be one the player could see
 * and not take. The strike budget at the only profile that grants an undo is
 * five, so the case is a rarity rather than a routine loss.
 */
function offerUndo(before, after, trace, effects) {
    if (trace.outcomeKind !== 'disaster')
        return after;
    if (after.end !== null)
        return after;
    if (!undoAvailable(after))
        return after;
    effects.push({
        type: 'UNDO_OFFERED',
        outcomeId: trace.outcomeId,
        remaining: after.settings.disasterUndoAllowance - after.progress.undosUsed,
    });
    return { ...after, undoOffer: { ...before, undoOffer: null } };
}
/**
 * Closes an open undo window.
 *
 * The offer stands while the player looks at the wreckage — reading a channel,
 * clearing the composer, asking for a hint — and closes the moment they move
 * the world on. Returns the same object when there is nothing to close, so an
 * illegal action is still a strict no-op.
 */
function closeUndoWindow(state) {
    return state.undoOffer === null ? state : { ...state, undoOffer: null };
}
/** Takes the undo: the pre-send state, restored, with the spend recorded on it. */
function undoSend(state) {
    const snapshot = state.undoOffer;
    if (snapshot === null)
        return unchanged(state);
    const restored = {
        ...snapshot,
        undoOffer: null,
        progress: { ...snapshot.progress, undosUsed: snapshot.progress.undosUsed + 1 },
    };
    return {
        state: restored,
        effects: [{ type: 'UNDO_TAKEN', undosUsed: restored.progress.undosUsed }],
        trace: null,
        traces: [],
    };
}
/**
 * One tick: queued posts and in-flight proposals move a step closer, anything
 * due lands, and any proposal whose window has closed resolves at its
 * destination.
 */
function advanceTick(loaded, state) {
    const effects = [];
    const traces = [];
    let next = {
        ...state,
        clock: { ...state.clock, tick: state.clock.tick + 1 },
        pip: { ...state.pip, composerLockTicks: Math.max(0, state.pip.composerLockTicks - 1) },
        pendingPosts: state.pendingPosts.map((pending) => ({
            ...pending,
            remainingTicks: pending.remainingTicks - 1,
        })),
        inFlight: state.inFlight.map((hop) => ({
            ...hop,
            remainingTicks: hop.remainingTicks - 1,
        })),
    };
    effects.push({ type: 'TICK_ELAPSED', tick: next.clock.tick });
    const delivered = deliverDuePosts(next);
    next = delivered.state;
    effects.push(...delivered.effects);
    const landing = next.inFlight.filter((hop) => hop.remainingTicks <= 0);
    if (landing.length === 0)
        return { state: next, effects, traces };
    next = { ...next, inFlight: next.inFlight.filter((hop) => hop.remainingTicks > 0) };
    for (const hop of landing) {
        if (next.end !== null)
            break;
        effects.push({ type: 'PROPOSAL_LANDED', hop });
        next = proposalAfterLanding(next, hop.toNodeId);
        // A hop is not the player speaking, so the identity on the trace is the
        // node that forwarded it. An `identity: 'self'` rule cannot fire on a hop.
        const forwarder = hop.fromNodeId === 'player' ? 'self' : puppetIdentity(hop.fromNodeId);
        const resolved = resolveAt(next, {
            loaded,
            nodeId: hop.toNodeId,
            channelId: hop.channelId,
            identity: forwarder,
            source: 'propagation',
            aggregate: next.proposal.tags,
            selection: [],
            reactToMessageId: null,
        });
        next = { ...resolved.state, lastTrace: resolved.trace };
        effects.push(...resolved.effects);
        traces.push(resolved.trace);
    }
    return { state: next, effects, traces };
}
/**
 * Wearing someone else's voice.
 *
 * The interesting case is the one the concept calls bandwidth. Trust is earned
 * per node, so several puppets can come due at once, but only three slots of
 * attention exist and a VP takes two of them. An acquisition that will not fit
 * is *held* rather than refused: the state records a pending acquisition, and
 * until the player releases somebody or abandons it, nothing else is legal.
 * Silently declining the switch would leave the player looking at a puppet they
 * unlocked, with no account of why they do not have it.
 */
function switchIdentity(loaded, state, identity) {
    if (state.identity === identity)
        return unchanged(state);
    const effects = [];
    let next = state;
    const nodeId = identityNodeId(identity);
    if (nodeId !== null) {
        if (!puppetSelectable(loaded, state, nodeId))
            return unchanged(state);
        const puppet = state.puppets[nodeId];
        if (puppet === undefined)
            return unchanged(state);
        if (!puppet.possessed) {
            const cost = bandwidthCostOf(loaded, nodeId) ?? 0;
            if (cost > state.bandwidth.capacity)
                return unchanged(state);
            if (cost > freeBandwidth(state)) {
                return {
                    state: { ...state, bandwidth: { ...state.bandwidth, pendingNodeId: nodeId } },
                    effects: [{ type: 'RELEASE_REQUIRED', nodeId, cost, free: freeBandwidth(state) }],
                    trace: null,
                    traces: [],
                };
            }
            next = takePossession(loaded, next, nodeId);
        }
        else {
            next = touchPuppet(next, nodeId);
        }
    }
    next = swapGrants(loaded, next, next.identity, identity, effects);
    next = {
        ...next,
        identity,
        composer: composerForIdentity(loaded, next.composer, identity),
    };
    effects.push({ type: 'IDENTITY_CHANGED', identity });
    return { state: next, effects, trace: null, traces: [] };
}
/**
 * Lets a puppet go. When an acquisition is pending, the freed bandwidth is spent
 * on it immediately — the release *was* the choice, and making the player then
 * repeat the switch would be asking the same question twice.
 */
function releasePuppet(loaded, state, nodeId) {
    const puppet = state.puppets[nodeId];
    if (puppet === undefined || !puppet.possessed)
        return unchanged(state);
    const effects = [];
    let next = releasePossession(loaded, state, nodeId, effects);
    effects.push({ type: 'PUPPET_RELEASED', nodeId });
    const pendingNodeId = next.bandwidth.pendingNodeId;
    if (pendingNodeId === null)
        return { state: next, effects, trace: null, traces: [] };
    const cost = bandwidthCostOf(loaded, pendingNodeId) ?? 0;
    if (cost > freeBandwidth(next) || !puppetSelectable(loaded, next, pendingNodeId)) {
        // Still short, or the acquisition lapsed while the choice was open. The
        // pending state stands and the player is asked again.
        return { state: next, effects, trace: null, traces: [] };
    }
    next = { ...next, bandwidth: { ...next.bandwidth, pendingNodeId: null } };
    next = takePossession(loaded, next, pendingNodeId);
    const identity = puppetIdentity(pendingNodeId);
    next = swapGrants(loaded, next, next.identity, identity, effects);
    next = { ...next, identity, composer: composerForIdentity(loaded, next.composer, identity) };
    effects.push({ type: 'IDENTITY_CHANGED', identity });
    return { state: next, effects, trace: null, traces: [] };
}
/** Walks away from a pending acquisition without releasing anybody for it. */
function cancelAcquisition(state) {
    const nodeId = state.bandwidth.pendingNodeId;
    if (nodeId === null)
        return unchanged(state);
    return {
        state: { ...state, bandwidth: { ...state.bandwidth, pendingNodeId: null } },
        effects: [{ type: 'ACQUISITION_CANCELLED', nodeId }],
        trace: null,
        traces: [],
    };
}
function send(loaded, state) {
    const targetNodeId = state.composer.targetNodeId;
    const channelId = state.activeChannelId;
    if (targetNodeId === null || channelId === null)
        return unchanged(state);
    if (!requiredSlotsFilled(state))
        return unchanged(state);
    if (state.pip.composerLockTicks > 0)
        return unchanged(state);
    const selection = selectionOf(state);
    const text = previewText(loaded, state);
    // 1. The world moves on before you speak: replies owed from earlier sends
    //    land above your new message, and anything in flight ticks down.
    const ticked = advanceTick(loaded, state);
    const effects = [...ticked.effects];
    const traces = [...ticked.traces];
    let next = ticked.state;
    if (next.end !== null) {
        return { state: next, effects, trace: null, traces };
    }
    // 2. Post, then let the room talk over it, then resolve.
    const post = { channel: channelId, authorNodeId: 'player', text };
    const posted = postMessage(next, post, 'player');
    next = posted.state;
    effects.push({ type: 'MESSAGE_POSTED', message: posted.message });
    const noise = emitNoise(loaded, next);
    next = noise.state;
    effects.push(...noise.effects);
    // Burial is measured against the message that was just posted: the noise
    // that lands on top of it is the difference between being read and being
    // scrolled past. At Intern this is always `null` — the chatter arrives, and
    // costs nothing.
    const buried = buriedOutcomeId(loaded, next, posted.message.id);
    if (buried !== null) {
        effects.push({
            type: 'MESSAGE_BURIED',
            messageId: posted.message.id,
            depth: burialDepth(next, posted.message.id),
            outcomeId: buried,
        });
    }
    next = proposalAfterSend(next, targetNodeId, next.identity);
    const resolved = resolveAt(next, {
        loaded,
        nodeId: targetNodeId,
        channelId,
        identity: next.identity,
        source: 'send',
        aggregate: aggregateTagSets(selection.map((slot) => loaded.particles[slot.particleId]?.tags)),
        selection,
        reactToMessageId: posted.message.id,
        buriedOutcomeId: buried,
    });
    next = resolved.state;
    effects.push(...resolved.effects);
    traces.push(resolved.trace);
    // 3. The assembly is spent; the target is not.
    next = afterPlayerResolution(loaded, next, resolved.trace, channelId, effects);
    next = offerUndo(state, next, resolved.trace, effects);
    return { state: next, effects, trace: resolved.trace, traces };
}
/**
 * The bookkeeping every player resolution owes, whether it went out as one
 * assembly or as a synchronised pitch: the counters the Quarterly Review reads,
 * the wariness the room accrued, and a fresh sheet in the composer.
 */
function afterPlayerResolution(loaded, state, trace, channelId, effects) {
    const golden = trace.outcomeKind === 'golden';
    let next = {
        ...state,
        composer: {
            ...state.composer,
            slots: state.composer.slots.map((slot) => ({ ...slot, particleId: null })),
        },
        progress: {
            ...state.progress,
            sends: state.progress.sends + 1,
            goldenSends: state.progress.goldenSends + (golden ? 1 : 0),
            nonGoldenSends: state.progress.nonGoldenSends + (golden ? 0 : 1),
            disasters: state.progress.disasters + (trace.outcomeKind === 'disaster' ? 1 : 0),
            targetsThisPhase: addSorted(state.progress.targetsThisPhase, [trace.nodeId]),
        },
        lastTrace: trace,
    };
    // Attention: speaking as a puppet, or to one, is what keeps it aligned.
    const speakingAs = identityNodeId(trace.identity);
    if (speakingAs !== null)
        next = touchPuppet(next, speakingAs);
    next = touchPuppet(next, trace.nodeId);
    const accrued = accrueSuspicion(loaded, next, trace, channelId);
    effects.push(...accrued.effects);
    return accrued.state;
}
/**
 * Wariness the player earned without any outcome saying so.
 *
 * Two engine-visible patterns, both scaled by the profile like an authored
 * delta and both switched off entirely for a package that declares no
 * `objectives.suspicion`:
 *
 *   - a **run of near misses** against one node. One near miss is a person
 *     getting it slightly wrong; three in a row is a person circling, and the
 *     node starts reading it that way. Any other resolution kind resets the run,
 *     so the pattern has to be an actual pattern.
 *   - **going out of band** — addressing someone in a DM or a private room
 *     rather than the channel the work lives in. It is the cheapest way to route
 *     around a blocker and the most obvious one to notice afterwards.
 *
 * A gated send accrues nothing here: it never reached the node, and the gate has
 * already charged for it in strikes.
 */
function accrueSuspicion(loaded, state, trace, channelId) {
    const declared = loaded.pack.objectives.suspicion;
    if (declared === undefined || trace.outcomeKind === 'gated') {
        return { state, effects: [] };
    }
    const nodeId = trace.nodeId;
    const runs = state.reputation.nearMissRuns;
    const run = trace.outcomeKind === 'nearmiss' ? (runs[nodeId] ?? 0) + 1 : 0;
    const required = declared.nearMissRun ?? 0;
    const completed = required > 0 && run >= required;
    let next = {
        ...state,
        reputation: {
            ...state.reputation,
            // The run resets on completion as well as on a different outcome, so a
            // fourth and fifth near miss do not each cost what the third did.
            nearMissRuns: { ...runs, [nodeId]: completed ? 0 : run },
        },
    };
    const effects = [];
    const outOfBand = loaded.channels[channelId]?.access !== 'public';
    const delta = (completed ? (declared.nearMissDelta ?? 0) : 0) +
        (outOfBand ? (declared.outOfBandDelta ?? 0) : 0);
    if (delta > 0) {
        const raised = withSuspicion(next, loaded, nodeId, delta);
        next = raised.state;
        effects.push(...raised.effects);
    }
    return { state: next, effects };
}
// ---------------------------------------------------------------------------
// The synchronised pitch
// ---------------------------------------------------------------------------
/** The mission's synchronised-pitch declaration, resolved, or `null` when it declares none. */
function syncPitchOf(loaded, state) {
    const declared = loaded.pack.objectives.synchronisedPitch;
    if (declared === undefined)
        return null;
    return {
        minIdentities: declared.minIdentities,
        // Undeclared, the ceiling is simply how many voices the player can hold.
        maxIdentities: declared.maxIdentities ?? state.bandwidth.capacity,
        targetNode: declared.targetNode,
        inChannel: declared.inChannel,
        requiresPuppets: declared.requiresPuppets ?? true,
    };
}
/**
 * Whether the current assembly may be set aside under the current identity.
 *
 * Staging is drafting, not sending: nothing is gated and nothing resolves, so
 * the only questions asked here are structural. One draft per identity, because
 * a synchronised pitch is several *people* agreeing at once — the same person
 * saying two things is a conversation, and the fiction has one of those already.
 */
export function canStageDraft(loaded, state) {
    const pitch = syncPitchOf(loaded, state);
    if (pitch === null)
        return false;
    if (state.staged.length >= pitch.maxIdentities)
        return false;
    if (state.pip.composerLockTicks > 0)
        return false;
    if (pitch.requiresPuppets && state.identity === 'self')
        return false;
    if (state.staged.some((draft) => draft.identity === state.identity))
        return false;
    return requiredSlotsFilled(state) && selectionOf(state).length > 0;
}
function stageDraft(loaded, state) {
    if (!canStageDraft(loaded, state))
        return unchanged(state);
    const draft = {
        identity: state.identity,
        selection: selectionOf(state),
        text: previewText(loaded, state),
    };
    const staged = [...state.staged, draft];
    return {
        state: {
            ...state,
            staged,
            composer: {
                ...state.composer,
                slots: state.composer.slots.map((slot) => ({ ...slot, particleId: null })),
            },
        },
        effects: [{ type: 'DRAFT_STAGED', identity: draft.identity, staged: staged.length }],
        trace: null,
        traces: [],
    };
}
function unstageDraft(state, identity) {
    if (!state.staged.some((draft) => draft.identity === identity))
        return unchanged(state);
    return {
        state: { ...state, staged: state.staged.filter((draft) => draft.identity !== identity) },
        effects: [{ type: 'DRAFT_UNSTAGED', identity }],
        trace: null,
        traces: [],
    };
}
/** True while enough drafts are staged for the pitch to go out. */
export function canExecuteSyncPitch(loaded, state) {
    const pitch = syncPitchOf(loaded, state);
    if (pitch === null)
        return false;
    if (state.staged.length < pitch.minIdentities)
        return false;
    if (state.channels[pitch.inChannel]?.unlocked !== true)
        return false;
    return loaded.nodes[pitch.targetNode] !== undefined;
}
/**
 * Everybody speaks at once.
 *
 * Each staged draft posts in its own voice, and then the *union* of every
 * draft's tags resolves once against the declared target — one proposal, not
 * three. That is the mechanical point of the manoeuvre: tags no single voice
 * could carry (an ops lead's data, a VP's mandate, a finance framing) arrive as
 * one multiset, so a rule no single assembly could satisfy fires.
 *
 * The gates run **once per draft, each in its own voice**, before any of it
 * lands. A pitch is only as good as its worst impression, so the first failure
 * is the pitch's failure — and it is that draft's puppet that pays the
 * credibility for it.
 *
 * It cannot be buried: several people posting in the same breath is not
 * something the room scrolls past.
 */
function executeSyncPitch(loaded, state) {
    const pitch = syncPitchOf(loaded, state);
    if (pitch === null || !canExecuteSyncPitch(loaded, state))
        return unchanged(state);
    const staged = state.staged;
    const lead = staged[0];
    if (lead === undefined)
        return unchanged(state);
    const combined = aggregateTagSets(staged.flatMap((draft) => draft.selection.map((slot) => loaded.particles[slot.particleId]?.tags)));
    // Slot numbers repeat across drafts — each was written in its own composer —
    // so a `slotParticle` leaf sees every draft's slot 2, and the trace's
    // contributing slots name positions rather than a single sentence. That is
    // the honest reading of several people speaking at once.
    const selection = staged.flatMap((draft) => draft.selection);
    // 1. The world moves on before anybody speaks, exactly as for a single send.
    const ticked = advanceTick(loaded, state);
    const effects = [...ticked.effects];
    const traces = [...ticked.traces];
    let next = ticked.state;
    if (next.end !== null)
        return { state: next, effects, trace: null, traces };
    // 2. Every draft goes up, in staging order, each under its own name.
    for (const draft of staged) {
        const post = { channel: pitch.inChannel, authorNodeId: 'player', text: draft.text };
        const posted = postMessage({ ...next, identity: draft.identity }, post, 'player');
        next = { ...posted.state, identity: next.identity };
        effects.push({ type: 'MESSAGE_POSTED', message: posted.message });
    }
    effects.push({
        type: 'SYNC_PITCH_EXECUTED',
        identities: staged.map((draft) => draft.identity),
        targetNodeId: pitch.targetNode,
    });
    // The room carries on around it, as it does around any send. Burial is not
    // measured, though: several people posting in the same breath is not
    // something the channel scrolls past.
    const noise = emitNoise(loaded, next);
    next = noise.state;
    effects.push(...noise.effects);
    // 3. Each voice is checked on its own words; the worst of them is the verdict.
    const gates = gatesForPitch(loaded, next, pitch, staged, combined);
    // Every voice that spoke has had the player's attention today, not only the
    // one the combined trace is attributed to.
    for (const draft of staged) {
        const nodeId = identityNodeId(draft.identity);
        if (nodeId !== null)
            next = touchPuppet(next, nodeId);
    }
    next = { ...next, staged: [] };
    next = proposalAfterSend(next, pitch.targetNode, lead.identity);
    const resolved = resolveAt(next, {
        loaded,
        nodeId: pitch.targetNode,
        channelId: pitch.inChannel,
        identity: lead.identity,
        source: 'sync',
        aggregate: combined,
        selection,
        reactToMessageId: null,
        gates,
    });
    next = resolved.state;
    effects.push(...resolved.effects);
    traces.push(resolved.trace);
    next = afterPlayerResolution(loaded, next, resolved.trace, pitch.inChannel, effects);
    next = offerUndo(state, next, resolved.trace, effects);
    return { state: next, effects, trace: resolved.trace, traces };
}
/**
 * Runs the gates once per staged draft and returns the first report that
 * failed, or the last that passed.
 *
 * Each draft is judged against its *own* selection and identity — a puppet's
 * voice mask is about the words that puppet is putting its name to — while the
 * channel, Cred, and PIP checks ask the same question every time and agree.
 */
function gatesForPitch(loaded, state, pitch, staged, combined) {
    let last = { results: [], failure: null };
    for (const draft of staged) {
        const context = evalContextFor(state, {
            loaded,
            nodeId: pitch.targetNode,
            channelId: pitch.inChannel,
            identity: draft.identity,
            source: 'sync',
            aggregate: combined,
            selection: draft.selection,
            reactToMessageId: null,
        });
        last = runGates(loaded, state, pitch.targetNode, pitch.inChannel, context);
        if (last.failure !== null)
            return last;
    }
    return last;
}
function harvest(loaded, state, messageId, highlightId) {
    const key = harvestKey(messageId, highlightId);
    if (state.harvested.includes(key))
        return unchanged(state);
    const message = state.messages.find((entry) => entry.id === messageId);
    if (message === undefined)
        return unchanged(state);
    if (state.channels[message.channelId]?.unlocked !== true)
        return unchanged(state);
    const highlight = message.highlights.find((entry) => entry.id === highlightId);
    if (highlight === undefined)
        return unchanged(state);
    const particleId = highlight.grantsParticle;
    let next = {
        ...state,
        harvested: sortedUnique([...state.harvested, key]),
        unlocked: { ...state.unlocked, particles: addSorted(state.unlocked.particles, [particleId]) },
    };
    // Harvest-to-slot: the token travels into the first empty slot that will take
    // it, which is what the animation in the interface depicts.
    const landing = next.composer.slots.find((slot) => slot.particleId === null && canSelect(loaded, next, slot.n, particleId)) ?? null;
    if (landing !== null) {
        next = {
            ...next,
            composer: {
                ...next.composer,
                slots: next.composer.slots.map((slot) => slot.n === landing.n ? { ...slot, particleId } : slot),
            },
        };
    }
    return {
        state: next,
        effects: [{ type: 'PARTICLE_HARVESTED', particleId, slot: landing?.n ?? null }],
        trace: null,
        traces: [],
    };
}
/**
 * `ADVANCE_PHASE` — the only action that moves game time.
 *
 * The order is deliberate. The world gets its turn first: the phase's scheduled
 * events fire and its chatter arrives *before* the player may act, and the
 * objectives are re-asked. Only then is the phase budget checked, so an Exec
 * Sync evaluation that wins the mission beats the clock that would otherwise
 * have ended it on the same advance.
 */
function advancePhase(loaded, state) {
    const clock = advanced(state.clock);
    const effects = [];
    let next = {
        ...state,
        clock,
        // A new phase is a fresh sheet: the composer lock lapses and the
        // per-phase target allowance resets.
        pip: { ...state.pip, composerLockTicks: 0 },
        progress: { ...state.progress, targetsThisPhase: [] },
    };
    effects.push({
        type: 'PHASE_ADVANCED',
        day: clock.day,
        phase: clock.phase,
        phasesRemaining: Math.max(0, next.settings.phaseBudget - clock.phasesUsed),
    });
    const entered = enterPhase(loaded, next);
    next = entered.state;
    effects.push(...entered.effects);
    if (clock.phasesUsed >= next.settings.phaseBudget && next.end === null) {
        const end = {
            result: 'loss',
            reason: 'clock_expired',
            summary: null,
            partialCredit: null,
        };
        next = { ...next, end };
        effects.push({ type: 'LEVEL_ENDED', end });
    }
    return { state: next, effects, trace: null, traces: [] };
}
/** True when the profile offers hints and the player has earned one. */
export function hintAvailable(state) {
    const threshold = state.settings.hintThreshold;
    if (threshold === null)
        return false;
    if (state.lastTrace === null)
        return false;
    return state.progress.nonGoldenSends >= threshold;
}
/**
 * The hint tier the next request is at, escalating with use and capped at 3.
 * Tier 3 names the slot that is costing the player; nothing ever states a
 * correct combination.
 */
function hintTier(state) {
    const used = state.progress.hintsUsed;
    return used <= 0 ? 1 : used === 1 ? 2 : 3;
}
function requestHint(loaded, state) {
    if (!hintAvailable(state))
        return unchanged(state);
    const hint = deriveHint(loaded, state, hintTier(state));
    return {
        state: { ...state, progress: { ...state.progress, hintsUsed: state.progress.hintsUsed + 1 } },
        effects: [{ type: 'HINT_OFFERED', hint }],
        trace: null,
        traces: [],
    };
}
/**
 * Advances a run by one action.
 *
 * The package is passed alongside the state rather than embedded in it: the
 * state is the few kilobytes that get saved and hashed, the package is the
 * immutable content it is read against.
 */
export function reduce(loaded, state, action) {
    if (state.end !== null)
        return unchanged(state);
    // A held acquisition is a forced choice: release somebody, or walk away from
    // the puppet. Nothing else is legal until one of those happens, so the player
    // is never left with a question on screen that the game has moved on from.
    if (state.bandwidth.pendingNodeId !== null) {
        if (action.type === 'RELEASE_PUPPET')
            return releasePuppet(loaded, state, action.nodeId);
        if (action.type === 'CANCEL_ACQUISITION')
            return cancelAcquisition(state);
        return unchanged(state);
    }
    switch (action.type) {
        case 'OPEN_CHANNEL': {
            const channel = state.channels[action.channelId];
            if (channel === undefined || !channel.unlocked)
                return unchanged(state);
            return {
                state: {
                    ...state,
                    activeChannelId: action.channelId,
                    channels: { ...state.channels, [action.channelId]: { ...channel, unread: 0 } },
                },
                effects: [{ type: 'CHANNEL_OPENED', channelId: action.channelId }],
                trace: null,
                traces: [],
            };
        }
        case 'HARVEST':
            return harvest(loaded, state, action.messageId, action.highlightId);
        case 'SELECT': {
            if (!canSelect(loaded, state, action.slot, action.particleId))
                return unchanged(state);
            return {
                state: {
                    ...state,
                    composer: {
                        ...state.composer,
                        slots: state.composer.slots.map((slot) => slot.n === action.slot ? { ...slot, particleId: action.particleId } : slot),
                    },
                },
                effects: [{ type: 'SLOT_CHANGED', slot: action.slot, particleId: action.particleId }],
                trace: null,
                traces: [],
            };
        }
        case 'CLEAR_SLOT': {
            const slot = slotAt(state, action.slot);
            if (slot === undefined || slot.particleId === null)
                return unchanged(state);
            return {
                state: {
                    ...state,
                    composer: {
                        ...state.composer,
                        slots: state.composer.slots.map((entry) => entry.n === action.slot ? { ...entry, particleId: null } : entry),
                    },
                },
                effects: [{ type: 'SLOT_CHANGED', slot: action.slot, particleId: null }],
                trace: null,
                traces: [],
            };
        }
        case 'CLEAR_ALL': {
            if (state.composer.slots.every((slot) => slot.particleId === null))
                return unchanged(state);
            return {
                state: {
                    ...state,
                    composer: {
                        ...state.composer,
                        slots: state.composer.slots.map((slot) => ({ ...slot, particleId: null })),
                    },
                },
                effects: state.composer.slots
                    .filter((slot) => slot.particleId !== null)
                    .map((slot) => ({ type: 'SLOT_CHANGED', slot: slot.n, particleId: null })),
                trace: null,
                traces: [],
            };
        }
        case 'SET_TARGET': {
            if (!targetable(loaded, state, action.nodeId))
                return unchanged(state);
            return {
                state: { ...state, composer: { ...state.composer, targetNodeId: action.nodeId } },
                effects: [{ type: 'TARGET_CHANGED', nodeId: action.nodeId }],
                trace: null,
                traces: [],
            };
        }
        case 'SWITCH_IDENTITY':
            return switchIdentity(loaded, state, action.identity);
        case 'RELEASE_PUPPET':
            return releasePuppet(loaded, state, action.nodeId);
        case 'CANCEL_ACQUISITION':
            return cancelAcquisition(state);
        case 'STAGE_DRAFT':
            return stageDraft(loaded, state);
        case 'UNSTAGE_DRAFT':
            return unstageDraft(state, action.identity);
        case 'EXECUTE_SYNC_PITCH':
            return executeSyncPitch(loaded, closeUndoWindow(state));
        case 'SEND':
            return send(loaded, closeUndoWindow(state));
        case 'UNDO_SEND':
            return undoSend(state);
        case 'WAIT': {
            const ticked = advanceTick(loaded, closeUndoWindow(state));
            const effects = [...ticked.effects];
            let next = ticked.state;
            // Waiting is not free: the tick that let a reply land also let the room
            // fill up, which is what the next send has to be heard over.
            if (next.end === null) {
                const noise = emitNoise(loaded, next);
                next = noise.state;
                effects.push(...noise.effects);
            }
            return { state: next, effects, trace: null, traces: ticked.traces };
        }
        case 'ADVANCE_PHASE':
            return advancePhase(loaded, closeUndoWindow(state));
        case 'REQUEST_HINT':
            return requestHint(loaded, state);
    }
}
/**
 * Whether a node may be addressed right now: it exists, it is addressable, the
 * player is looking at a channel it belongs to, and the phase's allowance of
 * distinct targets is not already spent on other people.
 */
export function targetable(loaded, state, nodeId) {
    const node = loaded.nodes[nodeId];
    if (node === undefined || node.addressable === false)
        return false;
    const channelId = state.activeChannelId;
    if (channelId === null)
        return false;
    if (state.channels[channelId]?.unlocked !== true)
        return false;
    if (loaded.channels[channelId]?.members.includes(nodeId) !== true)
        return false;
    const spent = state.progress.targetsThisPhase;
    return spent.includes(nodeId) || spent.length < state.settings.maxTargetsPerPhase;
}
//# sourceMappingURL=reduce.js.map